import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

const formatReportDate = (reportDate) => {
  if (reportDate instanceof Date) {
    return reportDate.toLocaleDateString();
  }

  if (reportDate) {
    const parsed = new Date(reportDate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString();
    }
  }

  return new Date().toLocaleDateString();
};

const CoverPage = ({
  primary = '#0A68FF',
  company = 'StealthNet AI',
  clientName = 'Client Name',
  reportDate = new Date(),
}) => {
  const primaryMid = darken(primary, 0.14);
  const primaryLight = lighten(primary, 0.3);
  const formattedDate = formatReportDate(reportDate);

  return (
    <Box
      id="cover-page"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '10.8in',
        height: '100%',
        overflow: 'hidden',
        color: '#ffffff',
        background: `
          radial-gradient(420px 320px at 88% 14%, ${alpha(primaryLight, 0.18)} 0%, transparent 68%),
          radial-gradient(520px 380px at 8% 92%, ${alpha(primary, 0.12)} 0%, transparent 70%),
          linear-gradient(150deg, #09111f 0%, #0a1322 44%, #0e1728 100%)
        `,
        px: { xs: '0.52in', sm: '0.62in', md: '0.72in' },
        py: { xs: '0.58in', sm: '0.68in', md: '0.74in' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -36,
          right: -42,
          width: 240,
          height: 240,
          borderRadius: '28px',
          border: `1px solid ${alpha('#dbeafe', 0.08)}`,
          background: `linear-gradient(180deg, ${alpha(primaryLight, 0.16)} 0%, ${alpha(primaryMid, 0.06)} 100%)`,
          transform: 'rotate(10deg)',
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: 72,
          left: 54,
          width: 240,
          height: 1,
          background: `linear-gradient(90deg, ${alpha('#ffffff', 0)} 0%, ${alpha('#dbeafe', 0.34)} 48%, ${alpha('#ffffff', 0)} 100%)`,
        }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          id="cover"
          sx={{
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            color: alpha('#dbeafe', 0.92),
          }}
        >
          {company || 'StealthNet AI'}
        </Typography>

        <Chip
          label="CONFIDENTIAL"
          size="small"
          sx={{
            height: 30,
            px: 0.5,
            bgcolor: '#b00020',
            color: '#ffffff',
            borderRadius: '999px',
            fontWeight: 800,
            letterSpacing: '0.14em',
            '& .MuiChip-label': {
              px: 1.4,
            },
          }}
        />
      </Stack>

      <Box sx={{ position: 'relative', zIndex: 1, mt: { xs: 9, sm: 11 }, maxWidth: 560 }}>
        <Typography
          sx={{
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: alpha('#cbd5e1', 0.98),
            mb: 1.5,
          }}
        >
          Assessment Report
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: { xs: '2.4rem', sm: '3rem', md: '3.5rem' },
            fontWeight: 800,
            lineHeight: 1.06,
            letterSpacing: '-0.035em',
            color: '#ffffff',
            mb: 2.25,
          }}
        >
          Vishing Assessment Report
        </Typography>

        <Box
          sx={{
            width: 170,
            height: 4,
            borderRadius: '999px',
            bgcolor: primary,
            mb: 2.5,
          }}
        />

        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            color: alpha('#dbeafe', 0.94),
            mb: 4,
          }}
        >
          Voice-based social engineering assessment and campaign review
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 5 }} sx={{ mb: 3.4 }}>
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: alpha('#94a3b8', 0.92),
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                mb: 0.55,
              }}
            >
              Prepared For
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#f8fafc',
              }}
            >
              {clientName || 'Client Name'}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: alpha('#94a3b8', 0.92),
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                mb: 0.55,
              }}
            >
              Date
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#f8fafc',
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {['Voice Phishing', 'Call Campaign Review', 'Social Engineering Simulation'].map((label) => (
            <Chip
              key={label}
              label={label}
              size="small"
              sx={{
                bgcolor: alpha(primary, 0.14),
                color: '#ffffff',
                border: `1px solid ${alpha(primaryLight, 0.26)}`,
                borderRadius: '999px',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 1.2,
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          mt: 'auto',
          pt: 6,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: alpha('#94a3b8', 0.92),
            letterSpacing: '0.02em',
          }}
        >
          Confidential vishing assessment material intended only for authorized recipients.
        </Typography>
      </Box>
    </Box>
  );
};

export default CoverPage;
